import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowsClockwise, ArrowLeft, TrendUp, TrendDown } from "@phosphor-icons/react";
import api from "../lib/api";
import { StreakChip } from "../components/StreakChip";

function StatBlock({ label, value, accent }) {
  return (
    <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</div>
      <div className={`font-display text-5xl leading-none mt-2 ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}

function StreakBar({ streak }) {
  const isWin = streak.type === "W";
  const isBlip = streak.is_blip;
  const width = Math.min(streak.length * 18 + 24, 240);
  return (
    <motion.div
      initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
      className="flex items-center gap-3 py-1"
      data-testid="streak-timeline-item"
    >
      <div className="text-[10px] font-mono-d text-zinc-600 w-24 shrink-0">
        {streak.start}{streak.end !== streak.start ? ` → ${streak.end}` : ""}
      </div>
      <div
        className={`h-7 flex items-center px-3 rounded-sm ${
          isBlip
            ? isWin ? "border border-[#007AFF]/40 text-[#007AFF]/80" : "border border-[#FF3B30]/40 text-[#FF3B30]/80"
            : isWin ? "bg-[#007AFF] text-white" : "border-2 border-[#FF3B30] text-[#FF3B30]"
        } font-display tracking-wider`}
        style={{ minWidth: isBlip ? 36 : width }}
      >
        {streak.type}{streak.length}
        {!isBlip && <span className="ml-2 text-[10px] font-mono-d opacity-70 uppercase">multi-game</span>}
      </div>
    </motion.div>
  );
}

export default function TeamDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/mlb/teams/${id}/streaks`);
      setData(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const cs = data?.current_streak;
  const sos = data?.streak_of_streaks;

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <Link to="/standings" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500 hover:text-white mb-6" data-testid="team-back-link">
        <ArrowLeft size={14} /> All Standings
      </Link>

      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] mb-1">Team {data?.season || ""}</div>
          <h1 className="font-display text-5xl sm:text-6xl leading-none" data-testid="team-detail-name">
            TEAM #{id}
          </h1>
          <p className="text-sm text-zinc-400 mt-2 font-mono-d">
            {data ? `${data.total_games} completed games` : "—"}
          </p>
        </div>
        <button onClick={load} data-testid="team-refresh-button"
          className="flex items-center gap-2 px-5 py-2.5 bg-transparent border border-white/20 hover:border-white/60 text-white rounded-sm text-xs uppercase tracking-wider font-bold hover:-translate-y-0.5 transition-all">
          <ArrowsClockwise size={16} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {!data ? (
        <div className="text-zinc-500 font-mono-d text-sm" data-testid="team-loading">Crunching streaks…</div>
      ) : (
        <>
          {/* HERO STATS */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <StatBlock label="W-L" value={`${data.totals.wins}-${data.totals.losses}`} />
            <div className="bg-[#121212] border border-white/10 rounded-sm p-5 flex flex-col justify-between">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Current Streak</div>
              <div className="mt-2" data-testid="team-current-streak">
                {cs ? (
                  <StreakChip type={cs.type} length={cs.length} blip={cs.is_blip} size="lg" glow={!cs.is_blip} />
                ) : <span className="text-zinc-500 font-mono-d">—</span>}
              </div>
            </div>
            <StatBlock
              label="Streak of Streaks"
              value={sos?.length || 0}
              accent={sos?.type === "W" ? "text-[#007AFF]" : sos?.type === "L" ? "text-[#FF3B30]" : "text-zinc-400"}
            />
            <StatBlock label="Blips · Multi" value={`${data.totals.single_game_blips} · ${data.totals.multi_game_streaks}`} />
          </div>

          {/* STREAK OF STREAKS callout */}
          <div className="bg-[#121212] border border-white/10 rounded-sm p-6 mb-10" data-testid="streak-of-streaks-card">
            <div className="flex items-center gap-3 mb-2">
              {sos?.type === "W" ? <TrendUp size={20} className="text-[#007AFF]" /> :
               sos?.type === "L" ? <TrendDown size={20} className="text-[#FF3B30]" /> : null}
              <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500">Streak of Streaks (multi-game runs only)</div>
            </div>
            <div className="font-display text-3xl">{sos?.description}</div>
            <p className="text-xs font-mono-d text-zinc-500 mt-2 max-w-2xl">
              We ignore single-game blips. Whenever a team strings together consecutive multi-game win-streaks (or loss-streaks) with only solo-game blips in between, we count it as a meta-streak.
            </p>

            {data.meta_runs && data.meta_runs.length > 0 && (
              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="meta-runs-grid">
                {data.meta_runs.map((m, i) => (
                  <div key={i} className={`border rounded-sm p-3 ${
                    m.type === "W" ? "border-[#007AFF]/40 bg-[#007AFF]/5" : "border-[#FF3B30]/40 bg-[#FF3B30]/5"
                  }`}>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{m.type === "W" ? "Win Run" : "Loss Run"}</div>
                    <div className="font-display text-3xl mt-1">{m.count}× <span className="text-zinc-500 text-base">({m.total_games}g)</span></div>
                    <div className="text-[10px] font-mono-d text-zinc-500 mt-1">{m.start} → {m.end}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* TIMELINE */}
          <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
            <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-4">Streak Timeline (oldest → newest)</div>
            <div className="space-y-1" data-testid="streak-timeline">
              {data.streaks.map((s, i) => <StreakBar key={i} streak={s} />)}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
